import Promise from 'bluebird';
import {
  getProduct,
  addAddress,
  availability,
  checkout
} from 'shared/models/api';
import { trackNow } from 'shared/models/analytic';
import { sanitizeAvailability } from 'shared/models/address';

const TOGGLE_GIFT = 'TOGGLE_GIFT';
const GIFT_RECIPIENT = 'GIFT_RECIPIENT';
const GIFT_EMAIL = 'GIFT_EMAIL';
const GIFT_MOBILE = 'GIFT_MOBILE';
const GIFT_NOTE = 'GIFT_NOTE';
const GIFT_OCCASION = 'GIFT_OCCASION';
const GIFT_BAG = 'GIFT_BAG';
const CHECKOUT_VENDOR = 'CHECKOUT_VENDOR';
const CHECKOUT_AVAILABILITY = 'CHECKOUT_AVAILABILITY';
const DELIVERY_TIME = 'DELIVERY_TIME';
const DELIVERY_DATE = 'DELIVERY_DATE';
const SHIPPING_OPTION = 'SHIPPING_OPTION';
const ORDER_ID = 'ORDER_ID';
const PAYMENT_METHOD = 'PAYMENT_METHOD';
const CHECKOUT_LOADING = 'CHECKOUT_LOADING';
const CHECKOUT_SUCCESS = 'CHECKOUT_SUCCESS';
const CHECKOUT_FAILED = 'CHECKOUT_FAILED';
const PAYMENT_REQUEST_LOADING = 'PAYMENT_REQUEST_LOADING';
const PAYMENT_REQUEST_LOADED = 'PAYMENT_REQUEST_LOADED';

const init = {
  isGift: false,
  giftRecipient: '',
  giftEmail: '',
  giftMobile: '',
  giftNote: '',
  giftOccasion: '',
  vendor: {},
  availability: { dates: [] },
  immediateSlot: { start: null, description: '' },
  deliveryTime: {},
  deliveryDate: {},
  shippingOption: null,
  paymentMethod: {},
  paymentRequestLoading: false,
  paymentRequestPayment: null,
  id: null,
  loading: false,
  error: null
};

export default function reducer(state = init, action = {}) {
  switch (action.type) {
    case TOGGLE_GIFT:
      return {
        ...state,
        isGift: action.isGift,
        immediateSlot:
          state.deliveryTime.start === null
            ? {
                start: null,
                description: action.isGift
                  ? 'Allow Recipient to Schedule Delivery'
                  : state.availability.selection_text
              }
            : state.immediateSlot
      };
    case GIFT_RECIPIENT:
      return { ...state, giftRecipient: action.name };
    case GIFT_EMAIL:
      return { ...state, giftEmail: action.email };
    case GIFT_MOBILE:
      return { ...state, giftMobile: action.number };
    case GIFT_NOTE:
      return { ...state, giftNote: action.note };
    case GIFT_OCCASION:
      return { ...state, giftOccasion: action.occasion };
    case GIFT_BAG:
      return { ...state, giftBag: action.bag };
    case CHECKOUT_VENDOR:
      return { ...state, vendor: action.vendor };
    case CHECKOUT_AVAILABILITY:
      return {
        ...state,
        availability: action.availability,
        immediateSlot:
          action.availability.selection_text && !state.isGift
            ? {
                ...state.immediateSlot,
                description: action.availability.selection_text
              }
            : state.immediateSlot
      };
    case DELIVERY_TIME:
      return { ...state, deliveryTime: action.deliveryTime };
    case DELIVERY_DATE:
      return { ...state, deliveryDate: action.deliveryDate };
    case SHIPPING_OPTION:
      return { ...state, shippingOption: action.shippingOption };
    case ORDER_ID:
      return { ...state, id: action.orderId };
    case PAYMENT_METHOD:
      return { ...state, paymentMethod: action.paymentMethod };
    case CHECKOUT_LOADING:
      return { ...state, loading: true, error: null };
    case CHECKOUT_SUCCESS:
      return { ...state, loading: false, error: null };
    case CHECKOUT_FAILED:
      return { ...state, loading: false, error: action.error };
    case PAYMENT_REQUEST_LOADING:
      return { ...state, paymentRequestLoading: true };
    case PAYMENT_REQUEST_LOADED:
      return {
        ...state,
        paymentRequestLoading: false,
        paymentRequestPayment: action.payment || null
      };
    default:
      return state;
  }
}

export function toggleGift() {
  return (dispatch, getState) => {
    const isGift = !getState().checkout.isGift;
    if (isGift) trackNow('Send as Gift Selected');
    else trackNow('Send as Gift Deselected');
    dispatch({ type: TOGGLE_GIFT, isGift });

    const deliveryTime = getState().checkout.deliveryTime || {};
    const immediateSlot = getState().checkout.immediateSlot;
    if (deliveryTime.start === null) {
      dispatch({ type: DELIVERY_TIME, deliveryTime: immediateSlot });
    }
  };
}

export function setGiftRecipient(name) {
  return dispatch => dispatch({ type: GIFT_RECIPIENT, name });
}

export function setGiftEmail(email) {
  return dispatch => dispatch({ type: GIFT_EMAIL, email });
}

export function setGiftMobile(number) {
  return dispatch => dispatch({ type: GIFT_MOBILE, number });
}

export function setGiftNote(note) {
  return dispatch => dispatch({ type: GIFT_NOTE, note });
}

export function setGiftOccasion(occasion) {
  return dispatch => dispatch({ type: GIFT_OCCASION, occasion });
}

export function getGiftBag() {
  return dispatch => {
    getProduct('GFP-BLACKST-750').then(bag => {
      dispatch({ type: GIFT_BAG, bag });
    });
  };
}

export function setVendor(vendor) {
  return (dispatch, getState) => {
    const prevVendor = getState().checkout.vendor;
    const prevStoreId = prevVendor && prevVendor.store_id;
    dispatch({ type: CHECKOUT_VENDOR, vendor });
    if (vendor.store_id !== prevStoreId) {
      return availability().then(availability => {
        availability = sanitizeAvailability(availability);
        dispatch({ type: CHECKOUT_AVAILABILITY, availability });
      });
    }
  };
}

export function selectDeliveryTime(deliveryTime) {
  return dispatch => dispatch({ type: DELIVERY_TIME, deliveryTime });
}

export function selectDeliveryDate(deliveryDate) {
  return dispatch => dispatch({ type: DELIVERY_DATE, deliveryDate });
}

export function setOrderId() {
  return (dispatch, getState) => {
    const user = getState().login.user;
    const orderId = [user.objectId, new Date().getTime()].join('-');
    dispatch({ type: ORDER_ID, orderId });
  };
}

export function setShippingOption(shippingOption) {
  return dispatch => {
    return Promise.resolve().then(() =>
      dispatch({ type: SHIPPING_OPTION, shippingOption })
    );
  };
}

export function resetDeliveryOptions() {
  return dispatch => {
    dispatch({ type: DELIVERY_TIME, deliveryTime: {} });
    dispatch({ type: DELIVERY_DATE, deliveryDate: {} });
    dispatch({ type: SHIPPING_OPTION, shippingOption: null });
  };
}

export function setPaymentMethod(paymentMethod) {
  return dispatch => {
    return Promise.resolve().then(() =>
      dispatch({ type: PAYMENT_METHOD, paymentMethod })
    );
  };
}

export function submit(params, onSuccess, onFail) {
  return dispatch => {
    dispatch({ type: CHECKOUT_LOADING });
    return checkout(params)
      .then(() => dispatch({ type: CHECKOUT_SUCCESS }))
      .then(() => (onSuccess ? onSuccess() : true))
      .catch(error => {
        const message = error && error.body && error.body.message;
        dispatch({ type: CHECKOUT_FAILED, error: message });
        trackNow('Order Failed', { reason: message });
        if (onFail) onFail();
        return Promise.reject(new Error(message));
      });
  };
}

function ensureAddressId(address, currentUser) {
  const clientId =
    address.clientId || `${currentUser.objectId}-${Date.now() / 1000}`;
  return { ...address, clientId };
}

export function saveAddress() {
  return (dispatch, getState) => {
    const state = getState();
    const currentUser = state.login.user;
    const address = ensureAddressId(state.address, currentUser);
    // eslint-disable-next-line no-console
    return addAddress(address).catch(error => console.log(error));
  };
}

export function loadPaymentRequest() {
  return dispatch => dispatch({ type: PAYMENT_REQUEST_LOADING });
}

export function setPaymentRequestPayment(meta) {
  return dispatch => {
    if (!meta) return dispatch({ type: PAYMENT_REQUEST_LOADED, payment: null });
    const payment = {
      id: 'paymentRequest',
      brand: meta.applePay ? 'Apple Pay' : "Your Device's Saved Payment"
    };
    return dispatch({ type: PAYMENT_REQUEST_LOADED, payment });
  };
}
